import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { ChevronDown, Check } from "lucide-react";
import fill_chevron_down from "../../assets/png/fill_chevron_down.png";
import close from "../../assets/png/close_filter.png";
import { apiCallGeneric } from "../../services/api-service.js";
import "./common.css";

const CommonDashDropdown = ({
  apiUrl,
  placeholder = "Select",
  onSelect,
  value,
  searchView = false,
}) => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchDebounceTimer = useRef(null);

  // Fetch from API

  const fetchData = async (term = "") => {
    if (!apiUrl) {
      setItems([]);
      return;
    }

    setLoading(true);

    try {
      const response = await apiCallGeneric("get", apiUrl, null, {
        params: { term },
      });

      if (response.status === 200 && response.data.success) {
        setItems(response.data.suggestionlist || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData("");
  }, [apiUrl]);

  // Sync selected value from parent
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // Handle search (hit API)
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    searchDebounceTimer.current = setTimeout(() => {
      fetchData(term);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Handle selection
  const handleSelect = (item) => {
    setSelected(item);
    if (onSelect) onSelect(item);
  };

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="success"
        id="dropdown-basic"
        className="backgroundDasGrey rounded-4 assigned-dropdowndasd"
        style={{
          width: "100%",
          height: "28px",
          border: "none",
          display: "flex",
          alignItems: "center", // vertical center
          justifyContent: "space-between", // text left, icon right
          padding: "0 8px",
        }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            color: "#212129",
            flex: 1, // take remaining space
            marginRight: "4px",
          }}
          className="fs-12 fw-400 textcolor"
        >
          {selected ? selected.label : placeholder}
        </span>

        <span
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
          className="fs-12 fw-400 textcolor"
        >
          {searchView === false ? (
            <ChevronDown />
          ) : (
            <img src={fill_chevron_down} alt="chev down" />
          )}
          {searchView === true && selected ? (
            <img
              onClick={() => handleSelect(null)}
              src={close}
              alt="x"
              style={{ cursor: "pointer" }}
            />
          ) : null}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="p-2 w-100"
        container={document.body}
        style={{
          maxHeight: "250px",
          overflowY: "auto",
          position: "absolute",
          width: "auto",
          minWidth: "250px",
        }}
      >
        {/* Search box */}
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          className="mb-2"
        />

        {/* Loading spinner */}
        {loading && (
          <div className="px-2 text-muted">
            <Spinner size="sm" /> Loading...
          </div>
        )}

        {/* Dropdown items */}
        {!loading && items.length > 0 ? (
          items.map((item) => (
            <Dropdown.Item
              key={item.value}
              onClick={() => handleSelect(item)}
              className="d-flex justify-content-between align-items-center text-black"
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                margin: "2px 0",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.15s ease-in-out",
                backgroundColor:
                  selected && selected.value === item.value
                    ? "#f0ebff"
                    : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!selected || selected.value !== item.value) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  selected && selected.value === item.value
                    ? "#f0ebff"
                    : "transparent";
              }}
            >
              <span>{item.label}</span>
              {selected && selected.value === item.value && (
                <Check size={16} color="#6c5ce7" strokeWidth={2.5} />
              )}
            </Dropdown.Item>
          ))
        ) : !loading ? (
          <div className="px-2 text-muted">No results found</div>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CommonDashDropdown;
